import React from 'react';
import { Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { media } from '../data/media';
import { useLanguage } from '../contexts/LanguageContext';
const cacaoGallery = [{
  src: media.cacaoPod,
  alt: 'Cabosse de cacao mûre sur l’arbre, dans une plantation tropicale'
}, {
  src: media.cacaoOpen,
  alt: 'Cabosse de cacao ouverte révélant la pulpe blanche et les fèves violettes'
}, {
  src: media.cacaoBeans,
  alt: 'Fèves de cacao torréfiées et éclats de cacao'
}];
const gallery = [{
  src: media.chocolatBars,
  alt: 'Barres de chocolat aux fruits et noix'
}, {
  src: media.bonbonsPralines,
  alt: 'Assortiment de chocolats pralinés emballés à la main'
}];
export function CacaoArtSection() {
  const {
    t
  } = useLanguage();
  return <section id="cacao" className="scroll-mt-32 bg-creme py-24 md:py-32" data-id="element-25" data-mp="3jcmao0wt1szh">
      <div className="mx-auto max-w-7xl px-6 md:px-12" data-id="element-26" data-mp="3oanpuar4185o">
        <div className="mx-auto mb-14 max-w-3xl text-center" data-id="element-27" data-mp="3ob79px0bm6mc">
          <span className="mb-4 block font-sans text-xs uppercase tracking-[0.22em] text-caramel" data-id="element-28" data-mp="x3av19s4lrq">
            {t.cacao.eyebrow}
          </span>
          <h2 className="mb-8 font-serif text-3xl text-cacao-900 md:text-5xl" data-id="element-29" data-mp="3s9ehjt3b9p2x">
            {t.cacao.titleLead}{' '}
            <span className="italic text-or" data-id="element-30" data-mp="3na1yzkjh3465">{t.cacao.titleAccent}</span>
          </h2>
          <p className="font-sans font-light leading-relaxed text-cacao-700" data-id="element-31" data-mp="3ib8j5lgpr12t">
            {t.cacao.intro}
          </p>
        </div>

        <div className="mb-16 grid gap-6 sm:grid-cols-3" data-id="element-32" data-mp="ew31ufkrlcr2">
          {cacaoGallery.map((item, index) => <motion.figure key={item.alt} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.12
        }} className="border border-or/40 p-2" data-id="element-33" data-mp="9wqfrx1pw9pj" data-mpi={index}>
              <img src={item.src} alt={item.alt} className="aspect-[4/3] w-full object-cover" data-id="element-34" data-mp="2qku7zx0342uu" />
            </motion.figure>)}
        </div>

        <div className="mb-16 grid gap-6 sm:grid-cols-2" data-id="element-35" data-mp="2x0b70fjulq9g">
          {gallery.map((item, index) => <motion.div key={item.alt} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.12
        }} className="overflow-hidden" data-id="element-36" data-mp="2i77oa4uit6h5" data-mpi={index}>
              <img src={item.src} alt={item.alt} className="aspect-[4/3] w-full object-cover" data-id="element-37" data-mp="1zevpfs32snbr" />
            </motion.div>)}
        </div>

        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20" data-id="element-38" data-mp="1qdff1kc9gl7j">
          <div data-id="element-39" data-mp="1qgbbf48gn1tv">
            <h3 className="mb-8 font-serif text-xl leading-snug text-cacao-900 md:text-2xl" data-id="element-40" data-mp="1ufizuzktl25h">
              {t.cacao.benefitsTitle}
            </h3>
            <ul className="space-y-4" data-id="element-41" data-mp="1bs1k3kja3jtb">
              {t.cacao.benefits.map((benefit, index) => <motion.li key={benefit} initial={{
              opacity: 0,
              x: -16
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.08
            }} className="flex items-center gap-4 border-b border-caramel/20 pb-4 font-sans font-light text-cacao-700" data-id="element-42" data-mp="16tgqw52g20ed" data-mpi={index}>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-caramel text-caramel" data-id="element-43" data-mp="1pqrhz2dx9niq">
                    <Check size={13} strokeWidth={2.5} aria-hidden="true" data-id="element-44" data-mp="1zh2xznavrn5z" />
                  </span>
                  {benefit}
                </motion.li>)}
            </ul>
          </div>

          <div className="flex flex-col gap-8" data-id="element-45" data-mp="28hr846x5l3dl">
            <div className="border border-caramel/30 bg-creme-dark p-8 md:p-10" data-id="element-46" data-mp="28j2bi5mos946">
              <span className="mb-4 block font-sans text-[11px] uppercase tracking-[0.2em] text-or" data-id="element-47" data-mp="2siu97q6vfdjy">
                {t.cacao.philosophyTitle}
              </span>
              <p className="mb-5 font-serif text-2xl italic text-caramel md:text-3xl" data-id="element-48" data-mp="31c7d1n6h3zkt">
                « {t.cacao.philosophyQuote} »
              </p>
              <p className="font-sans font-light leading-relaxed text-cacao-700" data-id="element-49" data-mp="36aihfie01hvn">
                {t.cacao.philosophyText}
              </p>
            </div>

            <div className="border-l-2 border-caramel pl-6 md:pl-8" data-id="element-50" data-mp="2dltj1r4bvqnl">
              <h3 className="mb-4 font-serif text-xl text-cacao-900" data-id="element-51" data-mp="2do64wfd7il5x">
                {t.cacao.heritageTitle}
              </h3>
              <p className="font-sans font-light leading-relaxed text-cacao-700" data-id="element-52" data-mp="2nl2948w2ol4o">
                {t.cacao.heritageText}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}